import { ApplicationError } from '../domain/errors';
import { InventoryAuthority } from '@qkindex/contracts';
import { normalizeLocationName } from '../domain/location';
import {
  LocationRepository,
  firebaseLocationRepository,
} from '../repositories/locationRepository';

export interface BulkLocationDeleteResult {
  deletedLocationIds: string[];
  failedLocationIds: string[];
}

export interface LocationService {
  create(
    authority: InventoryAuthority,
    name: string,
    description?: string,
  ): Promise<string>;
  rename(
    authority: InventoryAuthority,
    locationId: string,
    name: string,
    description?: string,
  ): Promise<void>;
  delete(
    authority: InventoryAuthority,
    locationId: string,
    legacyName: string,
  ): Promise<void>;
  deleteMany(
    authority: InventoryAuthority,
    locations: Array<{ id: string; name: string }>,
  ): Promise<BulkLocationDeleteResult>;
}

export const createLocationService = (
  repository: LocationRepository,
): LocationService => {
  const assertUnique = async (
    authority: InventoryAuthority,
    name: string,
    exceptId?: string,
  ) => {
    const matches = await repository.findByNormalizedName(
      authority,
      normalizeLocationName(name),
    );
    if (matches.some((id) => id !== exceptId)) {
      throw new ApplicationError(
        'conflict',
        'A location with this name already exists.',
      );
    }
  };
  return {
    async create(authority, name, description = '') {
      const trimmedName = name.trim();
      if (!trimmedName)
        throw new ApplicationError('validation', 'Location name is required.');
      await assertUnique(authority, trimmedName);
      return repository.create(authority, trimmedName, description.trim());
    },
    async rename(authority, locationId, name, description = '') {
      const trimmedName = name.trim();
      if (!trimmedName)
        throw new ApplicationError('validation', 'Location name is required.');
      await assertUnique(authority, trimmedName, locationId);
      await repository.update(authority, locationId, {
        name: trimmedName,
        description: description.trim(),
      });
    },
    async delete(authority, locationId, legacyName) {
      if (await repository.countItems(authority, locationId, legacyName)) {
        throw new ApplicationError(
          'conflict',
          'Move or delete the items in this location first.',
        );
      }
      await repository.delete(authority, locationId);
    },
    async deleteMany(authority, locations) {
      const counts = await Promise.all(
        locations.map((location) =>
          repository.countItems(authority, location.id, location.name),
        ),
      );
      if (counts.some((count) => count > 0)) {
        throw new ApplicationError(
          'conflict',
          'Move or delete the items in these locations first.',
        );
      }
      const outcomes: PromiseSettledResult<void>[] = [];
      for (const location of locations) {
        try {
          await repository.delete(authority, location.id);
          outcomes.push({ status: 'fulfilled', value: undefined });
        } catch (reason) {
          outcomes.push({ status: 'rejected', reason });
        }
      }
      return {
        deletedLocationIds: locations
          .filter((_, index) => outcomes[index].status === 'fulfilled')
          .map(({ id }) => id),
        failedLocationIds: locations
          .filter((_, index) => outcomes[index].status === 'rejected')
          .map(({ id }) => id),
      };
    },
  };
};

export const locationService = createLocationService(
  firebaseLocationRepository,
);
